import { useId, useState } from 'react';

import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import PlanCard, { CheckIcon, ScreenBadge } from '../components/PlanCard';
import Reveal from '../components/ui/Reveal';
import { Section, SectionHeading } from '../components/ui/Section';
import { LIFETIME_PLAN, PLAN_GROUPS } from '../data/pricingData';
import { PROMO, PROMO_MESSAGE } from '../config/promo';
import { whatsappLink } from '../config/site';
import { cn } from '../lib/utils';

import googlePay from '../assets/logo/Google_Pay.png';
import applePay from '../assets/logo/Apple_Pay.png';
import binance from '../assets/logo/Binance_Logo.svg.png';
import bancontact from '../assets/logo/Bancontact.png';
import mastercard from '../assets/logo/Mastercard.png';
import visa from '../assets/logo/Visa.png';

/* alt text now matches each image — Apple Pay was labelled "USDT" and
   Google Pay "PayPal" in the previous version. */
const PAYMENT_METHODS = [
    { src: visa, name: 'Visa' },
    { src: mastercard, name: 'Mastercard' },
    { src: applePay, name: 'Apple Pay' },
    { src: googlePay, name: 'Google Pay' },
    { src: bancontact, name: 'Bancontact' },
    { src: binance, name: 'Binance Pay' },
];

export default function Pricing() {
    const [group, setGroup] = useState('standard');
    const tabsId = useId();

    const plans = PLAN_GROUPS[group].plans;

    return (
        <Section id="forfaits">
            <SectionHeading
                eyebrow="Forfaits"
                title="Choisissez la formule qui vous correspond"
                description="Un paiement unique, aucun abonnement récurrent. Chaque forfait inclut le lecteur IBO Pro et la garantie de remboursement."
            />

            {/* Group switcher */}
            <div
                role="tablist"
                aria-label="Type de forfait"
                className="mx-auto mb-10 flex w-full max-w-sm rounded-full border border-line bg-surface-raised p-1"
            >
                {Object.entries(PLAN_GROUPS).map(([key, value]) => (
                    <button
                        key={key}
                        role="tab"
                        type="button"
                        id={`${tabsId}-${key}`}
                        aria-selected={group === key}
                        aria-controls={`${tabsId}-panel`}
                        onClick={() => setGroup(key)}
                        className={cn(
                            'flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-200',
                            group === key
                                ? 'bg-brand text-ink-950'
                                : 'text-content-muted hover:text-white',
                        )}
                    >
                        {value.label}
                    </button>
                ))}
            </div>

            <div
                id={`${tabsId}-panel`}
                role="tabpanel"
                aria-labelledby={`${tabsId}-${group}`}
                className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
            >
                {plans.map((plan) => (
                    // Keying on the plan id remounts the cards when the group
                    // switches, so the entry animation replays.
                    <PlanCard key={plan.id} plan={plan} />
                ))}
            </div>

            {/* Lifetime plan */}
            <Reveal className="mt-14">
                <Card
                    as="article"
                    highlighted
                    className="overflow-hidden p-7 md:p-10"
                >
                    <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
                        <div>
                            <Badge tone="solid">Accès à vie</Badge>
                            <h3 className="mt-4 text-heading font-bold text-white">{LIFETIME_PLAN.name}</h3>
                            <p className="mt-2 text-content-muted">
                                Un seul paiement, aucun renouvellement. La formule la plus économique sur
                                la durée.
                            </p>

                            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                {LIFETIME_PLAN.features.map((feature) => (
                                    <li key={feature} className="flex gap-2.5 text-sm text-content-muted">
                                        <CheckIcon className="mt-0.5 text-brand" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="rounded-xl border border-line bg-ink-950/50 p-6 text-center">
                            <div className="flex items-baseline justify-center gap-1">
                                <span className="text-5xl font-extrabold tracking-tight text-brand">
                                    {LIFETIME_PLAN.price}€
                                </span>
                            </div>
                            {LIFETIME_PLAN.originalPrice && (
                                <p className="mt-1.5 text-sm">
                                    <span className="text-content-subtle line-through">
                                        {LIFETIME_PLAN.originalPrice}€
                                    </span>
                                    <span className="ml-2 font-semibold text-brand">
                                        −{PROMO.discountEur}€
                                    </span>
                                </p>
                            )}
                            <p className="mt-1 text-sm text-content-muted">Paiement unique</p>

                            <div className="mt-4 flex justify-center">
                                <ScreenBadge count={LIFETIME_PLAN.screens} />
                            </div>

                            <Button href={LIFETIME_PLAN.link} size="lg" fullWidth className="mt-6">
                                Commander l'accès à vie
                            </Button>
                            <p className="mt-3 text-xs text-content-subtle">
                                Identifiants et guide envoyés par e-mail après paiement.
                            </p>
                        </div>
                    </div>
                </Card>
            </Reveal>

            {/* Payment methods */}
            <div className="mt-16">
                <h3 className="mb-6 text-center text-sm font-bold uppercase tracking-[0.14em] text-content-subtle">
                    Moyens de paiement acceptés
                </h3>
                <ul className="flex flex-wrap justify-center gap-3">
                    {PAYMENT_METHODS.map((method) => (
                        <li
                            key={method.name}
                            className="grid h-14 w-24 place-items-center rounded-xl border border-line bg-white/[0.04] px-4 transition-colors duration-300 hover:border-line-strong"
                        >
                            <img
                                src={method.src}
                                alt={method.name}
                                loading="lazy"
                                decoding="async"
                                className="max-h-7 w-full object-contain"
                            />
                        </li>
                    ))}
                </ul>
            </div>

            {/* Custom offer */}
            <Reveal className="mt-14">
                <Card className="flex flex-col items-center gap-6 p-7 text-center md:flex-row md:justify-between md:p-9 md:text-left">
                    <div>
                        <h3 className="text-title font-bold text-white">
                            Offre nouvelle saison : −{PROMO.discountEur}€ sur votre forfait
                        </h3>
                        <p className="mt-2 max-w-xl text-content-muted">
                            Écrivez-nous sur WhatsApp avant de commander pour obtenir votre remise, ou
                            pour un forfait sur mesure : durée spécifique, nombre d'écrans ou revente.
                        </p>
                    </div>
                    <Button
                        href={whatsappLink(PROMO_MESSAGE)}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="whatsapp"
                        size="lg"
                        className="shrink-0"
                    >
                        Obtenir mes −{PROMO.discountEur}€
                    </Button>
                </Card>
            </Reveal>
        </Section>
    );
}
